import React, { useEffect, useState, useRef } from 'react';
import { openInSim } from '../../utils/simHandoff';
import { openRuneFit } from '../../utils/fitHandoff';
import GradeBadge from '../../components/GradeBadge';
import ResultPill, { resultBoxStyle, ResultVs } from '../../components/ResultPill';
import { Post } from '../../types/Post';
import { getMonsterImgPath } from '../../utils/monster';

import ProfileWithName from '../../components/ProfileWithName';
import { getRelativeTime, isBattleRecord, monthDayTime, BATTLE_CARD_VER } from '../../utils/date';
import { usePostActions } from '../../hooks/usePosts';
import MonsterSelect from './MonsterSelect';
import { isOperator } from '../../utils/permission';
import { IconComment, IconLink, IconThought } from '../../components/Icons';
import { loadMonsterMapping } from '../../utils/monsterMapping';
import BattleRunes, { RunePick, battleBuilds, useCanSeeOpponentRunes } from '../BattleRunes';
import UploadCounter from './UploadCounter';
import { TacticBuild } from '../TacticBuild';
import { useBackHandler } from '../../hooks/useBackHandler';
import { guildImg } from '../../utils/guildImage';
interface Monster {
  name: string;
  attribute: string;
}

interface CounterPostProps {
  open: boolean;
  onClose: () => void;
  post: Post | null;
  monsterMapping: Record<string, any>;
  /** simViewer — 시뮬레이션 탭을 볼 수 있는 계정([시뮬레이션] 단추) */
  user: { uuid: string; nickname: string; role: string; profileImg: string; simViewer?: boolean };
  onPostDeleted?: () => void;
}

/**
 * **방어 기록이면 우리 방덱을 왼쪽(attackTeam)으로** 옮기고 `defenseLog` 를 붙인다.
 *
 * 길드원 탭은 열 때 옮겨 주지만, 다른 곳(전투기록 목록 등)에서 열면 표시가 없어 오른쪽에 빈 LOSE 상자와 VS 가 떴다
 * (사용자 2026-10-13). 열쇠 `siege-<점령전>-<공방>-<번호>` 의 공방 자리가 2 면 방어 기록이다. 이미 옮겨 온 것이면
 * attackTeam 이 우리 덱이고, 아니면 defenseTeam 이 우리 덱이다.
 */
export function asOwnSide(p: Post | null): Post | null {
  if (!p || p.defenseLog || !/^siege-\d+-2-\d+$/.test(String(p._id))) return p;
  const ours = (p.attackTeam || []).length ? p.attackTeam : p.defenseTeam || [];
  return { ...p, attackTeam: ours, defenseTeam: [], defenseLog: true };
}

const CounterPost: React.FC<CounterPostProps> = ({ open, onClose, post: initialPost, monsterMapping: initialMonsterMapping, user, onPostDeleted }) => {
  const [post, setPost] = useState<Post | null>(asOwnSide(initialPost));
  const [comments, setComments] = useState<any[]>([]);
  const [commentInput, setCommentInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [editMode, setEditMode] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editComment, setEditComment] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');
  const [monsterMapping, setMonsterMapping] = useState<Record<string, any>>(initialMonsterMapping || {});
  const [mappingLoaded, setMappingLoaded] = useState(false);

  // 몬스터 수정 관련 상태
  const [editAttackTeam, setEditAttackTeam] = useState<Monster[]>([]);
  const [editDefenseTeam, setEditDefenseTeam] = useState<Monster[]>([]);
  const [monsterSelectOpen, setMonsterSelectOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState<'attack' | 'defense'>('attack');
  const [editingSlot, setEditingSlot] = useState<number>(0);
  /** 전투기록에서 룬을 볼 몹 — 위쪽 아이콘을 누르면 바뀐다. 처음은 공덱 첫 마리 */
  const [runePick, setRunePick] = useState<RunePick>({ side: 'attack', idx: 0 });
  // 상대 덱 룬을 볼 수 있나 — 오른쪽 아이콘을 누를 수 있게 할지
  const seeOpponentRunes = useCanSeeOpponentRunes();
  /**
   * 이 전투로 공략 쓰기 — 연 순간의 룬 세팅(공덱 셋 + 방덱 셋). null 이면 닫혀 있다.
   * 룬을 아직 받지 않은 전투면 칸이 비어 있고 사람이 적는다.
   */
  const [tacticFromBattle, setTacticFromBattle] = useState<Array<TacticBuild | null> | null>(null);

  // 공통 핸들러 훅 사용
  const { handleAddComment, handleDeleteComment } = usePostActions({
    postId: post?._id ?? '',
    user,
    setComments,
  });

  // 모달 닫기 핸들러 (드롭다운도 함께 닫음)
  const handleClose = () => {
    setDropdownOpen(false);
    setEditMode(false);
    onClose();
  };

  // 몬스터 매핑 로드
  useEffect(() => {
    if (open) {
      // 이미 매핑이 있으면 로드된 것으로 간주
      if (initialMonsterMapping && Object.keys(initialMonsterMapping).length > 0 && initialMonsterMapping.monsters) {
        setMonsterMapping(initialMonsterMapping);
        setMappingLoaded(true);
      } else if (!mappingLoaded) {
        // 매핑이 없으면 API에서 로드
        loadMonsterMapping()
          .then(data => {
            setMonsterMapping(data);
            setMappingLoaded(true);
          })
          .catch(error => {
            setMappingLoaded(true); // 에러가 나도 계속 진행
          });
      }
    } else {
      setMappingLoaded(false);
    }
  }, [open, initialMonsterMapping]);

  // 최신 게시글 데이터 fetch
  useEffect(() => {
    // 전투기록(SiegeBattle)은 게시물이 아니라 좋아요·댓글 개념이 없다.
    // _id 가 `siege-<logId>` 합성값이라 게시물 API 에 넣으면 안 된다.
    const isSiegeRecord = typeof initialPost?._id === 'string' && initialPost._id.startsWith('siege-');
    setRunePick({ side: 'attack', idx: 0 });
    if (open && initialPost?._id && !isSiegeRecord) {
      fetch(`/api/posts/posting/${initialPost._id}`, { credentials: 'include' })
        .then(res => res.json())
        .then(data => {
          setPost(data);
        });
      fetch(`/api/posts/comments?id=${initialPost._id}`, { credentials: 'include' })
        .then(res => res.json())
        .then(data => setComments(data.comments || []))
        .catch(error => {});
    } else {
      setPost(asOwnSide(initialPost));
      setComments([]);
      setCommentInput('');
      setError('');
      setDropdownOpen(false);
      setEditMode(false);
    }
  }, [open, initialPost]);


  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }
    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  // 수정 버튼 클릭 시
  const handleEditClick = () => {
    setEditTitle(post?.title || '');
    setEditComment(post?.comment || '');
    const attackTeam = [...(post?.attackTeam || [])];
    while (attackTeam.length < 3) attackTeam.push({ name: '', attribute: '' });
    const defenseTeam = [...(post?.defenseTeam || [])];
    while (defenseTeam.length < 3) defenseTeam.push({ name: '', attribute: '' });
    setEditAttackTeam(attackTeam);
    setEditDefenseTeam(defenseTeam);
    setEditMode(true);
    setDropdownOpen(false);
  };

  // 몬스터 선택 핸들러
  const handleMonsterSelect = (monster: any) => {
    if (editingTeam === 'attack') {
      const newTeam = [...editAttackTeam];
      newTeam[editingSlot] = { name: monster.name, attribute: monster.attribute };
      setEditAttackTeam(newTeam);
    } else {
      const newTeam = [...editDefenseTeam];
      newTeam[editingSlot] = { name: monster.name, attribute: monster.attribute };
      setEditDefenseTeam(newTeam);
    }
    setMonsterSelectOpen(false);
  };

  // 몬스터 제거 핸들러
  const handleMonsterRemove = (team: 'attack' | 'defense', idx: number) => {
    if (team === 'attack') {
      const newTeam = [...editAttackTeam];
      newTeam[idx] = { name: '', attribute: '' };
      setEditAttackTeam(newTeam);
    } else {
      const newTeam = [...editDefenseTeam];
      newTeam[idx] = { name: '', attribute: '' };
      setEditDefenseTeam(newTeam);
    }
  };

  // 저장 버튼 클릭 시
  const handleEditSave = async () => {
    if (!editTitle.trim()) {
      setEditError('제목을 입력하세요.');
      return;
    }

    // 몬스터 검증
    const validAttackTeam = editAttackTeam.filter(m => m.name);
    const validDefenseTeam = editDefenseTeam.filter(m => m.name);

    if (validAttackTeam.length < 1) {
      setEditError('공덱은 최소 1개의 몬스터가 필요합니다.');
      return;
    }
    if (validDefenseTeam.length < 1) {
      setEditError('방덱은 최소 1개의 몬스터가 필요합니다.');
      return;
    }

    setEditLoading(true);
    setEditError('');
    try {
      const res = await fetch(`/api/posts/${post?._id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: editTitle,
          comment: editComment,
          attackTeam: validAttackTeam,
          defenseTeam: validDefenseTeam
        })
      });
      const data = await res.json();
      if (data.success) {
        setPost(data.post);
        setEditMode(false);
      } else {
        setEditError(data.error || '수정 실패');
      }
    } catch (e) {
      setEditError('수정 중 오류 발생');
    }
    setEditLoading(false);
  };

  // 게시글 삭제 핸들러
  const handleDeletePost = async () => {
    if (!post?._id || !window.confirm('정말로 이 게시글을 삭제하시겠습니까?')) return;
    
    try {
      const res = await fetch(`/api/posts/${post._id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (res.ok) {
        alert('게시글이 삭제되었습니다.');
        handleClose(); // 모달 닫기 (드롭다운도 함께 닫음)
        // 부모 컴포넌트에 삭제 완료 알림
        if (onPostDeleted) {
          onPostDeleted();
        }
      } else {
        const data = await res.json();
        alert(data.message || '삭제에 실패했습니다.');
      }
    } catch (error) {
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  // 뒤로 가기: 몹 고르기 · 공략 쓰기 창 → 수정 모드 순으로 물린다 (글 창 자체는 연 쪽이 등록한다)
  useBackHandler(open && editMode, () => { setEditMode(false); setEditError(''); });
  useBackHandler(open && monsterSelectOpen, () => setMonsterSelectOpen(false));
  useBackHandler(open && !!tacticFromBattle && !!user, () => setTacticFromBattle(null));

  if (!open || !post) return null;
  const date = new Date(post.createdAt);
  const now = new Date();
  const isToday = date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth() && date.getDate() === now.getDate();
  /**
   * 전투기록은 **언제나 초까지** 보여준다.
   *
   * 글은 예전 그대로다(오늘이면 시:분, 아니면 날짜만). 그런데 전투기록에 그 규칙을
   * 쓰면 어제 전투는 날짜만 남아 몇 시에 쳤는지 알 수 없다 — 하루에 수백 건이 쌓이는
   * 자료라 시각이 곧 정보다.
   */
  const dateStr = isBattleRecord(post)
    ? monthDayTime(post.createdAt)
    : isToday
      ? `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`
      : date.toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\./g, '.').replace(/\s/g, '').replace(/\.$/, '');
  const isOwner = user.nickname === post.nickname;
  const isAdmin = isOperator(user);

  /**
   * 전투기록이면 그 logId. 전투기록은 사람이 쓴 글이 아니라 **본문·댓글·수정이 없고**, 그 자리에
   * 그 전투에서 쓴 룬을 보여 준다(사용자 지시). 위쪽 몹 아이콘을 누르면 그 몹의 룬으로 바뀐다.
   */
  // `siege-<siegeId>-<logType>-<logId>` — 뒤가 전투 열쇠다(전투 번호만으로는 다른 점령전 전투와 겹친다)
  const siegeLogId = typeof post._id === 'string' && post._id.startsWith('siege-') ? post._id.slice(6) || null : null;
  const pickedMob: any = siegeLogId
    ? (runePick.side === 'attack' ? post.attackTeam : post.defenseTeam)[runePick.idx] || null
    : null;
  const pickedLook = pickedMob ? getMonsterImgPath(monsterMapping, pickedMob) : null;
  const pickable = (side: 'attack' | 'defense', idx: number) => {
    // 오른쪽(상대 덱)은 정해진 사람만 — 서버도 그 사람이 아니면 상대 룬을 내려주지 않는다
    if (!siegeLogId || (side === 'defense' && !seeOpponentRunes)) {
      return { onClick: undefined, title: undefined, className: undefined as string | undefined, style: {} as React.CSSProperties };
    }
    const on = runePick.side === side && runePick.idx === idx;
    return {
      onClick: () => setRunePick({ side, idx }),
      title: '눌러서 이 몹의 룬 보기',
      // 고른 몹 — 바깥 노란 테 대신 칸 테두리를 금색으로(App.css .mob-picked, 사용자 2026-10-28 A안)
      className: on ? 'mob-picked' : undefined,
      style: { cursor: 'pointer', borderRadius: 12 } as React.CSSProperties
    };
  };

  return (
    <div style={{
      position: 'fixed',
      left: 0,
      top: 0,
      width: '100vw',
      height: '100vh',
      zIndex: 3000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backdropFilter: 'blur(10px)',
      background: 'rgba(0, 0, 0, 0.5)',
      padding: '20px'
    }}>
      
      <div style={{
        background: '#fff',
        borderRadius: 24,
        minWidth: 480,
        maxWidth: 680,
        width: '90vw',
        // 위 72 — 보라 띠(56) 아래로 (공략·공지 팝업과 같은 차림, 사용자 2026-10-28 "전투기록 팝업도 이런 스타일로")
        padding: '72px 32px 32px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2), 0 8px 32px rgba(102, 126, 234, 0.15)',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        border: '1px solid rgba(0, 0, 0, 0.1)',
        zIndex: 2,
        maxHeight: '90vh',
        overflowY: 'auto',
        overflowX: 'hidden',
        boxSizing: 'border-box'
      }} onClick={e => e.stopPropagation()}>
        {/* 위 보라 띠 — 제목(흰 글자)과 단추가 여기에 올라온다 (공략·공지 팝업과 같게) */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 56, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }} />
        {/* 제목과 버튼들을 담는 컨테이너 */}
        {editMode ? (
          <input
            value={editTitle}
            onChange={e => setEditTitle(e.target.value)}
            style={{
              fontSize: 28,
              fontWeight: 700,
              width: '100%',
              margin: '0 0 20px 0',
              padding: '12px 16px',
              borderRadius: 12,
              border: '2px solid #e0e0e0',
              outline: 'none',
              background: '#fff',
              transition: 'all 0.3s ease'
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = '#667eea';
              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = '#e0e0e0';
              e.currentTarget.style.boxShadow = 'none';
            }}
            maxLength={40}
            autoFocus
          />
        ) : (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: 0,
            position: 'relative',
            width: '100%'
          }}>
            {/* 제목 — 보라 띠 안. 길면 말줄임, 마우스를 올리면 전체 */}
            <div title={post.title} style={{
              position: 'absolute',
              left: -8,
              right: 140,
              top: -72,
              height: 56,
              display: 'flex',
              alignItems: 'center',
              fontSize: 19,
              fontWeight: 800,
              color: '#fff',
              whiteSpace: 'nowrap',
              overflow: 'hidden'
            }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{post.title}</span>
            </div>

            {/* 버튼들 — 보라 띠 오른쪽 (창 오른쪽 끝에서 16 안쪽) */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 3,
              position: 'absolute',
              right: -16,
              top: -64
            }}>
              {/* 더보기 메뉴 */}
              {(isOwner || isAdmin) && (
                <div style={{ position: 'relative', zIndex: 10 }} ref={dropdownRef}>
                  <button
                    onClick={() => setDropdownOpen(v => !v)}
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 8,
                      background: 'transparent',
                      border: 'none',
                      fontSize: 18,
                      cursor: 'pointer',
                      color: '#fff',
                      padding: 0,
                      margin: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.3s ease'
                    }}
                    title="더보기"
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                      e.currentTarget.style.color = '#fff';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = '#fff';
                    }}
                  >
                    <span style={{ fontSize: 20, fontWeight: 700, letterSpacing: 1, lineHeight: 1 }}>⋮</span>
                  </button>
                  {dropdownOpen && (
                    <div style={{
                      position: 'absolute',
                      top: 50,
                      right: 0,
                      background: 'rgba(255, 255, 255, 0.95)',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      borderRadius: 12,
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
                      minWidth: 70,
                      zIndex: 100,
                      display: 'flex',
                      flexDirection: 'column',
                      backdropFilter: 'blur(20px)',
                      overflow: 'hidden'
                    }}>

                      {/* 전투기록은 고칠 본문이 없다 — 삭제만 남긴다 */}
                      {(isOwner || isAdmin) && !siegeLogId && (
                        <button style={{
                          padding: '12px 20px',
                          border: 'none',
                          background: 'none',
                          cursor: 'pointer',
                          fontSize: 15,
                          textAlign: 'left',
                          color: '#333',
                          fontWeight: 500,
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(10, 37, 159, 0.1)';
                          e.currentTarget.style.color = '#667eea';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'none';
                          e.currentTarget.style.color = '#333';
                        }}
                        onClick={handleEditClick}>수정</button>
                      )}
                      {(isOwner || isAdmin) && (
                        <button style={{
                          padding: '12px 20px',
                          border: 'none',
                          background: 'none',
                          cursor: 'pointer',
                          fontSize: 15,
                          color: '#d32f2f',
                          textAlign: 'left',
                          fontWeight: 500,
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(211, 47, 47, 0.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'none';
                        }}
                        onClick={handleDeletePost}>삭제</button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* URL 복사 버튼 */}
              <button
                onClick={() => {
                  /**
                   * 전투기록은 끝에 `?v=` 를 붙인다 — 카톡이 주소마다 미리보기를 오래 저장해 두어, 카드 모양을 바꿔도 같은 주소면
                   * 옛 카드가 떴다. 숫자는 서버 `utils/battleCard.ts` 의 CARD_VER 와 맞춘다.
                   */
                  const url = `${window.location.origin}/post/${post._id}${isBattleRecord(post) ? `?v=${BATTLE_CARD_VER}` : ''}`;
                  if (navigator.clipboard && navigator.clipboard.writeText) {
                    navigator.clipboard.writeText(url).then(() => {
                      alert('게시글 URL이 복사되었습니다!');
                    }).catch(() => {
                      alert('URL 복사에 실패했습니다.');
                    });
                  } else {
                    // 대체 방법: 텍스트 선택 및 복사
                    const textArea = document.createElement('textarea');
                    textArea.value = url;
                    document.body.appendChild(textArea);
                    textArea.select();
                    try {
                      document.execCommand('copy');
                      alert('게시글 URL이 복사되었습니다!');
                    } catch (err) {
                      alert('URL 복사에 실패했습니다.');
                    }
                    document.body.removeChild(textArea);
                  }
                }}
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 8,
                  background: 'transparent',
                  border: 'none',
                  fontSize: 16,
                  cursor: 'pointer',
                  color: '#fff',
                  padding: 0,
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.3s ease'
                }}
                title="URL 복사"
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.color = '#fff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = '#fff';
                }}
              >
                <IconLink />
              </button>

              {/* 닫기 버튼 */}
              <button
                onClick={handleClose}
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 8,
                  background: 'transparent',
                  border: 'none',
                  fontSize: 24,
                  color: '#fff',
                  cursor: 'pointer',
                  fontWeight: 700,
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.color = '#fff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = '#fff';
                }}
              >×</button>
            </div>
          </div>
        )}

        {/* 프로필/닉네임/작성일 */}
        <div style={{
          width: '100%',
          marginBottom: 12
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 20,
            width: '100%',
            background: '#fff',
            padding: '12px 0',
            borderBottom: '1px solid #e0e0e0'
          }}>
            {/* 프로필 이미지 + 닉네임/조회수 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {/* 네모난 프로필 이미지 */}
              <img loading="lazy"
                src={
                  post.profileImg || '/images/default-profile.png'
                }
                alt="프로필"
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 8,
                  objectFit: 'cover',
                  border: '2px solid rgba(102, 126, 234, 0.3)',
                  background: '#fff'
                }}
                onError={(e) => { (e.target as HTMLImageElement).src = '/images/default-profile.png'; }}
              />

              {/* 닉네임과 조회수 */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: post.isDeleted ? '#ff6b6b' : '#1976d2',
                  textAlign: 'left'
                }}>
                  {post.nickname}
                </div>
                <div style={{
                  fontSize: 14,
                  color: '#888',
                  fontWeight: 500,
                  textAlign: 'left'
                }}>
                  {/* 시각을 여기 붙인다. `dateStr` 은 예전부터 계산만 해 두고 아무 데도
                      쓰지 않아서, 팝업에는 시각이 통째로 빠져 있었다.
                      전투기록은 조회수가 뜻이 없으니(게임에서 자동으로 들어온 자료다) 시각만 둔다 */}
                  {isBattleRecord(post)
                    ? dateStr
                    : `조회수 ${typeof post.views === 'number' ? post.views : 0}${dateStr ? ` · ${dateStr}` : ''}`}
                </div>
              </div>
            </div>

            {/* 공덱 점수 — 작성자 줄 오른쪽 끝(사용자 2026-10-26). 이 판 룬으로 시뮬레이션 300번 돌린 등급(서버 utils/simGrade) */}
            {siegeLogId && post.simGrade && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, fontSize: 14, fontWeight: 700, color: '#667eea' }}>
                <span>공덱 점수</span>
                <GradeBadge grade={post.simGrade} size={26} />
              </div>
            )}
          </div>
        </div>

        {/* 공덱/방덱 */}
        <div style={{
          width: '100%',
          marginBottom: 16,
          paddingBottom: 16
        }}>
          {/* 길드 정보 */}
          {post.siegeWar && (
            <div style={{
              marginBottom: 16,
              position: 'relative'
            }}>
              {/* 상단 장식선 */}
              <div style={{
                height: 2,
                background: 'linear-gradient(90deg, transparent, #1976d2 30%, #d32f2f 70%, transparent)',
                marginBottom: 16,
                opacity: 0.3
              }} />

              {/* 길드 프로필 영역 */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: 48,
                fontSize: 14,
                fontWeight: 600,
                color: '#333',
                position: 'relative'
              }}>
                {/* VS 텍스트 */}
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#999',
                  backgroundColor: '#fff',
                  padding: '4px 12px',
                  borderRadius: 12,
                  border: '1px solid #e0e0e0',
                  zIndex: 1
                }}>
                  VS
                </div>

                {/* 설탕 길드 */}
                <div style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8
                }}>
                  <img loading="lazy"
                    src={guildImg('설탕')}
                    alt="설탕"
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      objectFit: 'cover',
                      border: '2px solid #1976d2',
                      boxShadow: '0 2px 8px rgba(25, 118, 210, 0.2)'
                    }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = '/images/unknown2.png';
                    }}
                  />
                  <span style={{ color: '#1976d2' }}>설탕</span>
                </div>

                {/* 상대 길드 */}
                <div style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8
                }}>
                  <img loading="lazy"
                    src={guildImg(post.siegeWarImg || post.siegeWar)}
                    alt={post.siegeWar}
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      objectFit: 'cover',
                      border: '2px solid #d32f2f',
                      boxShadow: '0 2px 8px rgba(211, 47, 47, 0.2)'
                    }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = '/images/unknown2.png';
                    }}
                  />
                  <span style={{ color: '#d32f2f' }}>{post.siegeWar}</span>
                </div>
              </div>

              {/* 하단 장식선 */}
              <div style={{
                height: 2,
                background: 'linear-gradient(90deg, transparent, #1976d2 30%, #d32f2f 70%, transparent)',
                marginTop: 16,
                opacity: 0.3
              }} />
            </div>
          )}

          <div style={{
            display: 'flex',
            flexDirection: 'row',
            gap: 48,
            width: '100%',
            justifyContent: 'center',
            alignItems: 'flex-start'
          }}>
            {/* 공덱 */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 , ...resultBoxStyle(post.result, true, editMode)}}>
              <ResultPill result={post.result} ours off={editMode} />
              <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', justifyContent: 'center' }}>
                {editMode ? (
                  editAttackTeam.map((m: any, idx: number) => (
                    <div key={idx} style={{ position: 'relative', cursor: 'pointer' }} onClick={() => {
                      setEditingTeam('attack');
                      setEditingSlot(idx);
                      setMonsterSelectOpen(true);
                    }}>
                      {m.name ? (
                        <>
                          <div style={{
                            width: 60,
                            height: 60,
                            border: '2px solid #1976d2',
                            borderRadius: 8,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: '#fff',
                            position: 'relative'
                          }}>
                            <img loading="lazy"
                              src={`/mobs/v2/${getMonsterImgPath(monsterMapping, m).imgPath?.replace(/\\/g, '/')}`}
                              alt={m.name}
                              style={{ width: 54, height: 54, objectFit: 'contain' }}
                            />
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMonsterRemove('attack', idx);
                            }}
                            style={{
                              position: 'absolute',
                              top: -6,
                              right: -6,
                              width: 20,
                              height: 20,
                              borderRadius: '50%',
                              background: '#f44336',
                              color: '#fff',
                              border: 'none',
                              fontSize: 12,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >×</button>
                        </>
                      ) : (
                        <div style={{
                          width: 60,
                          height: 60,
                          border: '2px dashed #1976d2',
                          borderRadius: 8,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#1976d2',
                          fontSize: 24
                        }}>+</div>
                      )}
                    </div>
                  ))
                ) : (
                  post.attackTeam.map((m: any, idx: number) => {
                    const { imgPath, displayName } = getMonsterImgPath(monsterMapping, m);
                    const pk = pickable('attack', idx);
                    return (
                      <div key={idx} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '0 0 auto', gap: 4 }}>
                        <div onClick={pk.onClick} title={pk.title} className={pk.className} style={{ position: 'relative', width: 60, height: 60, ...pk.style }}>
                          {imgPath ? (
                            <span
                              style={{
                                display: 'inline-block',
                                width: 60,
                                height: 60,
                                borderRadius: 10,
                                padding: 0,
                                // 전투기록 창은 몹 칸 빛을 뺀다(사용자 2026-10-27) — 공략·글쓰기 창만 App.css 의 빛 테두리
                                position: 'relative',
                              }}
                            >
                              <img loading="lazy"
                                src={`/mobs/v2/${imgPath.replace(/\\/g, '/')}`}
                                alt={displayName}
                                style={{
                                  width: 54,
                                  height: 54,
                                  objectFit: 'contain',
                                  borderRadius: 8,
                                  margin: 3,
                                  background: '#fff',
                                  display: 'block',
                                  boxShadow: '0 2px 8px #1976d222',
                                  transition: 'opacity 0.3s',
                                }}
                              />
                            </span>
                          ) : (
                            <div style={{ width: 60, height: 60, background: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 10, fontSize: 13, color: '#aaa', border: '1.5px solid #ddd' }}>
                              없음
                            </div>
                          )}
                        </div>
                        {imgPath ? (
                          <div style={{ fontSize: 11, color: '#333', fontWeight: 600, textAlign: 'center', maxWidth: 70, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</div>
                        ) : null}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
            {/*
              방어 기록에는 상대가 어떤 덱으로 쳤는지가 없다(게임이 안 준다).
              그래서 상대 칸을 아예 그리지 않는다 — 빈 칸을 두면 우리 덱이 상대 것처럼 읽힌다.
            */}
            {!post?.defenseLog && <ResultVs result={post.result} off={editMode} />}
            {!post?.defenseLog && (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 , ...resultBoxStyle(post.result, false, editMode)}}>
                <ResultPill result={post.result} ours={false} off={editMode} />
                <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', justifyContent: 'center' }}>
                  {editMode ? (
                    editDefenseTeam.map((m: any, idx: number) => (
                      <div key={idx} style={{ position: 'relative', cursor: 'pointer' }} onClick={() => {
                        setEditingTeam('defense');
                        setEditingSlot(idx);
                        setMonsterSelectOpen(true);
                      }}>
                        {m.name ? (
                          <>
                            <div style={{
                              width: 60,
                              height: 60,
                              border: '2px solid #d32f2f',
                              borderRadius: 8,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: '#fff',
                              position: 'relative'
                            }}>
                              <img loading="lazy"
                                src={`/mobs/v2/${getMonsterImgPath(monsterMapping, m).imgPath?.replace(/\\/g, '/')}`}
                                alt={m.name}
                                style={{ width: 54, height: 54, objectFit: 'contain' }}
                              />
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMonsterRemove('defense', idx);
                              }}
                              style={{
                                position: 'absolute',
                                top: -6,
                                right: -6,
                                width: 20,
                                height: 20,
                                borderRadius: '50%',
                                background: '#f44336',
                                color: '#fff',
                                border: 'none',
                                fontSize: 12,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >×</button>
                          </>
                        ) : (
                          <div style={{
                            width: 60,
                            height: 60,
                            border: '2px dashed #d32f2f',
                            borderRadius: 8,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#d32f2f',
                            fontSize: 24
                          }}>+</div>
                        )}
                      </div>
                    ))
                  ) : (
                    post.defenseTeam.map((m: any, idx: number) => {
                      const { imgPath, displayName } = getMonsterImgPath(monsterMapping, m);
                      const pk = pickable('defense', idx);
                      return (
                        <div key={idx} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '0 0 auto', gap: 4 }}>
                          <div onClick={pk.onClick} title={pk.title} className={pk.className} style={{ position: 'relative', width: 60, height: 60, ...pk.style }}>
                            {imgPath ? (
                              <span
                                style={{
                                  display: 'inline-block',
                                  width: 60,
                                  height: 60,
                                  borderRadius: 10,
                                  padding: 0,
                                  // 전투기록 창은 몹 칸 빛을 뺀다(사용자 2026-10-27) — 공략·글쓰기 창만 App.css 의 빛 테두리
                                  position: 'relative',
                                }}
                              >
                                <img loading="lazy"
                                  src={`/mobs/v2/${imgPath.replace(/\\/g, '/')}`}
                                  alt={displayName}
                                  style={{
                                    width: 54,
                                    height: 54,
                                    objectFit: 'contain',
                                    borderRadius: 8,
                                    margin: 3,
                                    background: '#fff',
                                    display: 'block',
                                    boxShadow: '0 2px 8px #d32f2f22',
                                    transition: 'opacity 1s',
                                  }}
                                />
                              </span>
                            ) : (
                              <div style={{ width: 60, height: 60, background: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 10, fontSize: 13, color: '#aaa', border: '1.5px solid #ddd' }}>
                                없음
                              </div>
                            )}
                          </div>
                          {imgPath ? (
                            <div style={{ fontSize: 11, color: '#333', fontWeight: 600, textAlign: 'center', maxWidth: 70, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</div>
                          ) : null}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 게시글 본문 — 전투기록이면 대신 그 전투에서 쓴 룬 */}
        {siegeLogId ? (
          <div style={{ width: '100%', marginBottom: 20 }}>
            <BattleRunes
              logId={siegeLogId}
              pick={runePick}
              defenseLog={post.defenseLog}
              attribute={pickedMob?.attribute}
              name={pickedLook?.displayName || pickedMob?.name || ''}
              img={pickedLook?.imgPath ? `/mobs/v2/${pickedLook.imgPath.replace(/\\/g, '/')}` : null}
              isMobile={false}
            />
            {/* 이 전투로 공략 쓰기 — 방어 기록은 상대 덱을 모르니 공격 기록만 */}
            {user && !post.defenseLog && (
              <button
                type="button"
                onClick={async () => setTacticFromBattle(await battleBuilds(siegeLogId, post.attackTeam, post.defenseTeam))}
                // 연보라 바탕 + 보라 테두리 — 흰 바탕은 다크모드에서 판에 묻혔고, 보라로 채우니 너무 튀었다(사용자 2026-10-22)
                style={{ marginTop: 10, width: '100%', padding: '12px 0', borderRadius: 12, border: '1.5px solid rgba(102, 126, 234, 0.6)', background: 'rgba(102, 126, 234, 0.14)', color: '#667eea', fontWeight: 800, fontSize: 15, cursor: 'pointer' }}
              >이 전투로 공략 쓰기</button>
            )}
            {/* 내 룬으로 맞추기 — 이 공덱 룬작에 내 룬을 룬 옵티마이저 [룬피팅] 탭에서 맞춘다 (사용자 2026-10-27) */}
            {user && !post.defenseLog && (
              <button
                type="button"
                onClick={async () => {
                  const builds = await battleBuilds(siegeLogId, post.attackTeam, post.defenseTeam);
                  openRuneFit({ label: '전투기록 공덱', mobs: (post.attackTeam as any[]).slice(0, 3), builds: builds.slice(0, 3) });
                  onClose();
                }}
                style={{ marginTop: 10, width: '100%', padding: '12px 0', borderRadius: 12, border: '1.5px solid rgba(102, 126, 234, 0.6)', background: 'rgba(102, 126, 234, 0.14)', color: '#667eea', fontWeight: 800, fontSize: 15, cursor: 'pointer' }}
              >내 룬으로 맞추기</button>
            )}
            {/* 시뮬레이션 — 권한 있는 사람만(사용자 2026-10-22). 이 전투를 시뮬레이션 탭에 [전투기록] 불러오기처럼 넣는다 */}
            {user?.simViewer && !post.defenseLog && (
              <button
                type="button"
                onClick={() => {
                  // 전투기록 글은 Post 꼴에 없는 칸(상대 닉네임·몹 번호)을 더 들고 온다(SiegeBattleList toPost)
                  const p: any = post;
                  openInSim({
                    ref: siegeLogId as string, logId: p.logId, result: p.result, battledAt: p.createdAt,
                    wizardName: p.nickname, oppWizardName: p.defenderNickname, oppGuildName: p.siegeWarImg,
                    attackTeam: p.attackTeam, defenseTeam: p.defenseTeam
                  });
                }}
                style={{ marginTop: 8, width: '100%', padding: '12px 0', borderRadius: 12, border: '1.5px solid rgba(102, 126, 234, 0.6)', background: 'rgba(102, 126, 234, 0.14)', color: '#667eea', fontWeight: 800, fontSize: 15, cursor: 'pointer' }}
              >시뮬레이션</button>
            )}
          </div>
        ) : (
        <div style={{ width: '100%', marginBottom: 20 }}>
          {editMode ? (
            <div style={{ position: 'relative', width: '100%' }}>
              <textarea
                value={editComment}
                onChange={e => setEditComment(e.target.value)}
                style={{
                  fontSize: 17,
                  color: '#333',
                  minHeight: 120,
                  width: '100%',
                  padding: '16px 20px',
                  resize: 'vertical',
                  transition: 'all 0.3s ease',
                  fontFamily: 'inherit',
                  lineHeight: 1.8,
                  borderRadius: 16,
                  border: '2px solid #e0e0e0',
                  background: '#fff'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#667eea';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#e0e0e0';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                maxLength={1000}
              />
              {editError && <div style={{ color: '#d32f2f', marginTop: 12, fontSize: 14, fontWeight: 500 }}>{editError}</div>}
            </div>
          ) : (
            <div style={{
              background: '#fff',
              padding: '20px 0',
              fontSize: 17,
              color: '#333',
              lineHeight: 1.9,
              textAlign: 'left',
              minHeight: 120,
              width: '100%',
              whiteSpace: 'pre-line',
              maxHeight: `calc(1.9em * 16)`,
              overflowY: 'auto',
              borderBottom: '1px solid #e0e0e0'
            }}>
              {post.comment}
            </div>
          )}
          {editMode && (
          <div style={{ position: 'absolute', right: 12, bottom: 12, display: 'flex', gap: 8 }}>
            <button
              onClick={() => {
                setEditMode(false);
                setEditError('');
              }}
              disabled={editLoading}
              style={{
                background: 'rgba(102, 126, 234, 0.1)',
                color: '#667eea',
                border: '2px solid #667eea',
                borderRadius: 12,
                padding: '8px 20px',
                fontWeight: 700,
                fontSize: 15,
                cursor: 'pointer',
                opacity: editLoading ? 0.5 : 1,
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                if (!editLoading) {
                  e.currentTarget.style.background = 'rgba(102, 126, 234, 0.2)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }
              }}
              onMouseLeave={(e) => {
                if (!editLoading) {
                  e.currentTarget.style.background = 'rgba(102, 126, 234, 0.1)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }
              }}
            >취소</button>
            <button
              onClick={handleEditSave}
              disabled={editLoading}
              style={{
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                color: '#fff',
                border: 'none',
                borderRadius: 12,
                padding: '8px 20px',
                fontWeight: 700,
                fontSize: 15,
                cursor: 'pointer',
                opacity: editLoading ? 0.7 : 1,
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
              }}
              onMouseEnter={(e) => {
                if (!editLoading) {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (!editLoading) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
                }
              }}
            >저장</button>
          </div>
          )}
        </div>

        )}

        {/* 댓글 목록 — 전투기록에는 없다 */}
        {!editMode && !siegeLogId && (
        <>
        <div style={{
          width: '100%',
          textAlign: 'left',
          background: '#fff',
          padding: '16px 0',
          marginBottom: 16
        }}>
          <div style={{
            fontWeight: 800,
            marginBottom: 16,
            color: '#333',
            fontSize: 20,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            letterSpacing: '0.3px'
          }}>
            <IconComment /> 댓글 ({comments.length})
          </div>
          {comments.length === 0 && <div style={{
            color: '#888',
            fontSize: 16,
            textAlign: 'center',
            padding: '32px',
            background: 'rgba(102, 126, 234, 0.05)',
            borderRadius: 12,
            border: '1px solid rgba(102, 126, 234, 0.1)',
            lineHeight: 1.6
          }}><IconThought /> 아직 댓글이 없습니다.</div>}
          {comments.map((c: any, i: number) => {
            const canDelete = (user.nickname && c.nickname === user.nickname) || isOperator(user);
            return (
              <div key={i} style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 16,
                padding: '12px 0',
                fontSize: 16,
                textAlign: 'left',
                position: 'relative',
                borderBottom: i < comments.length - 1 ? '1px solid rgba(0, 0, 0, 0.08)' : 'none'
              }}>
                {/* 프로필 이미지 */}
                <div style={{ flex: 'none' }}>
                  {c.profileImg ? (
                    <img loading="lazy"
                      src={
                        c.profileImg
                      }
                      alt="프로필"
                      style={{ width: 42, height: 42, borderRadius: '50%', objectFit: 'cover', border: '2px solid #e3f0ff', background: '#fff' }}
                      onError={e => { (e.target as HTMLImageElement).src = '/images/default-profile.png'; }}
                    />
                  ) : (
                    <img loading="lazy" src={'/images/default-profile.png'} alt="프로필" style={{ width: 42, height: 42, borderRadius: '50%', objectFit: 'cover', border: '2px solid #e3f0ff', background: '#fff' }} />
                  )}
                </div>
                {/* 닉네임, 날짜, 내용 */}
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontWeight: 700, color: c.isDeleted ? '#ff6b6b' : '#222', fontSize: 17 }}>{c.nickname}</span>
                    <span style={{ color: '#999', fontSize: 14, marginTop: 2 }}>{c.createdAt ? getRelativeTime(c.createdAt) : ''}</span>
                    {canDelete && (
                      <button onClick={() => handleDeleteComment(i, c)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#d32f2f', fontWeight: 700, fontSize: 20, cursor: 'pointer' }}>×</button>
                    )}
                  </div>
                  <div style={{ marginTop: 8, whiteSpace: 'pre-line', fontSize: 16, color: '#333', lineHeight: 1.7 }}>{c.content}</div>
                </div>
              </div>
            );
          })}
        </div>
        {/* 댓글 입력 */}
        <div style={{
          width: '100%',
          display: 'flex',
          gap: 16,
          background: '#fff',
          padding: '12px 0'
        }}>
          <input
            type="text"
            value={commentInput}
            onChange={e => setCommentInput(e.target.value)}
            placeholder="댓글을 입력하세요"
            style={{
              flex: 1,
              padding: '14px 18px',
              borderRadius: 12,
              border: '2px solid #e0e0e0',
              fontSize: 16,
              outline: 'none',
              background: '#fff',
              transition: 'all 0.3s ease'
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = '#667eea';
              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = '#e0e0e0';
              e.currentTarget.style.boxShadow = 'none';
            }}
            onKeyDown={e => { if (e.key === 'Enter') handleAddComment(commentInput, setCommentInput, setLoading, setError); }}
            disabled={loading}
          />
          <button
            onClick={() => handleAddComment(commentInput, setCommentInput, setLoading, setError)}
            disabled={loading || !commentInput.trim()}
            style={{
              padding: '14px 28px',
              borderRadius: 12,
              background: loading || !commentInput.trim()
                ? 'rgba(102, 126, 234, 0.3)'
                : 'linear-gradient(135deg, #667eea, #764ba2)',
              color: '#fff',
              border: 'none',
              fontWeight: 700,
              cursor: loading || !commentInput.trim() ? 'not-allowed' : 'pointer',
              fontSize: 16,
              transition: 'all 0.3s ease',
              boxShadow: loading || !commentInput.trim()
                ? 'none'
                : '0 4px 12px rgba(102, 126, 234, 0.3)',
              flexShrink: 0
            }}
            onMouseEnter={(e) => {
              if (!loading && commentInput.trim()) {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.4)';
              }
            }}
            onMouseLeave={(e) => {
              if (!loading && commentInput.trim()) {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
              }
            }}
          >등록</button>
        </div>
        {error && <div style={{ color: 'red', marginTop: 4 }}>{error}</div>}
        </>
        )}
      </div>

      {/* 이 전투로 공략 쓰기 — 전투기록 창 위에 띄운다(쓰기 창 자체는 z-index 100 이라 감싼다) */}
      {tacticFromBattle && user && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 3100 }}>
          <UploadCounter
            author={user.nickname}
            initialAttack={post.attackTeam as any}
            initialDefense={post.defenseTeam as any}
            initialBuilds={tacticFromBattle}
            onClose={() => setTacticFromBattle(null)}
            onUpload={() => { setTacticFromBattle(null); }}
          />
        </div>
      )}

      {/* 몬스터 선택 모달 */}
      {monsterSelectOpen && (
        <MonsterSelect
          open={monsterSelectOpen}
          monsterMapping={monsterMapping}
          onClose={() => setMonsterSelectOpen(false)}
          onSelect={handleMonsterSelect}
        />
      )}
    </div>
  );
};

// 프로필 이미지: 실패 시 unknown.png
const ProfileImg: React.FC<{ src: string; style?: React.CSSProperties }> = ({ src, style }) => {
  const [error, setError] = React.useState(false);
  if (error) {
    return (
      <img loading="lazy"
        src={'/images/default-profile.png'}
        alt="unknown"
        style={{ width: 18, height: 18, borderRadius: '50%', objectFit: 'cover', border: '2.5px solid #e3f0ff', background: '#e3f0ff', boxShadow: '0 2px 8px #1976d211', marginRight: 2, verticalAlign: 'middle', ...style }}
      />
    );
  }
  return (
    <img loading="lazy"
      src={src}
      alt="프로필"
      style={{ width: 18, height: 18, borderRadius: '50%', objectFit: 'cover', border: '2.5px solid #e3f0ff', background: '#fff', boxShadow: '0 2px 8px #1976d211', marginRight: 2, verticalAlign: 'middle', ...style }}
      onError={() => setError(true)}
    />
  );
};

export default CounterPost; 